import { AutoComplete, Col, Form, Input, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField } from '../../../../../contextRedux/Company/CompanyModel';
import { Information } from '../../../../../DTOS/CompanyInformation/Information';
import CompanyInformationService from '../../../../../services/companyInformation/CompanyInformationService';
import { RootState } from '../../../../../store';
import './ClientDetailsTab.css';

const { Option } = AutoComplete;

const ClientDetailsTab: FC = () => {
  const [form] = useForm();
  const dispatch = useDispatch();

  const company = useSelector((state: RootState) => state.models.company);
  const accountManagers = useSelector((state: RootState) => state.store.accountManager.data || []);
  const companyGroups = useSelector((state: RootState) => state.store.companyGroup.data || []);
  const tiers = useSelector((state: RootState) => state.store.companyTier.data || []);

  const initialValues = {
    id: useSelector((state: RootState) => state?.models?.company?.id),
    accountManagerId: useSelector((state: RootState) => state?.models?.company?.accountManagerId),
    companyGroupId: useSelector((state: RootState) => state?.models?.company?.companyGroupId),
    enumCompanyRankId: useSelector((state: RootState) => state?.models?.company?.enumCompanyRankId),
    type: useSelector((state: RootState) => state?.models?.company?.type),
    name: useSelector((state: RootState) => state?.models?.company?.name),
    country: useSelector((state: RootState) => state?.models?.company?.country),
    locality: useSelector((state: RootState) => state?.models?.company?.locality),
    address: useSelector((state: RootState) => state?.models?.company?.address),
    phone: useSelector((state: RootState) => state?.models?.company?.phone),
    email: useSelector((state: RootState) => state?.models?.company?.email),
    bankAccount: useSelector((state: RootState) => state?.models?.company?.bankAccount),
    bank: useSelector((state: RootState) => state?.models?.company?.bank),
    cnp: useSelector((state: RootState) => state?.models?.company?.cnp),
    signature: useSelector((state: RootState) => state?.models?.company?.signature),
    cui: useSelector((state: RootState) => state?.models?.company?.cui),
    registrationNumber: useSelector((state: RootState) => state?.models?.company?.registrationNumber),
    activityDescription: useSelector((state: RootState) => state?.models?.company?.activityDescription),
    leadership: useSelector((state: RootState) => state?.models?.company?.leadership),
    webAddress: useSelector((state: RootState) => state?.models?.company?.webAddress),
  };
  const handleCUIChange = (value: string) => {
    const specification: Information = {
      cui: value,
    };
    CompanyInformationService.informations(specification)
      .then((data: any) => {
        if (data.length === 1) {
          const fieldsValue = {
            ...form.getFieldsValue(true),
            name: data[0]?.name,
            address: data[0]?.fullAddress,
            registrationNumber: data[0]?.registrationCode,
          };

          form.setFieldsValue(fieldsValue);
          dispatch(updateField({ field: 'name', value: data[0]?.name }));
          dispatch(updateField({ field: 'address', value: data[0]?.fullAddress }));
          dispatch(
            updateField({
              field: 'registrationNumber',
              value: data[0]?.registrationCode,
            })
          );
        } else {
          const fieldsValue = {
            ...form.getFieldsValue(true),
            name: null,
            address: null,
            registrationNumber: null,
          };
          form.setFieldsValue(fieldsValue);
          dispatch(updateField({ field: 'name', value: null }));
          dispatch(updateField({ field: 'address', value: null }));
          dispatch(
            updateField({
              field: 'registrationNumber',
              value: null,
            })
          );
        }
      })
      .catch(() => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          name: null,
          address: null,
          registrationNumber: null,
        };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'name', value: null }));
        dispatch(updateField({ field: 'address', value: null }));
        dispatch(
          updateField({
            field: 'registrationNumber',
            value: null,
          })
        );
      });
  };

  const handleBankAccountChange = (value: string) => {
    const specification: Information = {
      iban: value,
    };
    CompanyInformationService.bank(specification)
      .then((data: any) => {
        const fieldsValue = {
          ...form.getFieldsValue(true),
          bank: data?.bank_name,
        };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'bank', value: data?.bank_name }));
      })
      .catch(() => {
        const fieldsValue = { ...form.getFieldsValue(true), bank: null };
        form.setFieldsValue(fieldsValue);
        dispatch(updateField({ field: 'bank', value: null }));
      });
  };

  const handleSelectGroupName = (value: string) => {
    const companyGroup: any = companyGroups?.find((group: any) => group.name === value);

    form.setFieldsValue({
      ...form.getFieldsValue(true),
      companyGroupId: companyGroup?.id,
      companyGroup: companyGroup,
    });

    dispatch(updateField({ field: 'companyGroupId', value: companyGroup?.id }));
    dispatch(updateField({ field: 'companyGroup', value: companyGroup }));
  };

  const handleSelectTier = (value?: string) => {
    const tier: any = tiers?.find((tier: { name: string }) => tier.name === value);

    if (value === null || tier) {
      const fieldsValue = {
        ...form.getFieldsValue(true),
        enumCompanyRankId: tier?.id,
        enumCompanyRank: tier,
      };
      form.setFieldsValue(fieldsValue);

      dispatch(updateField({ field: 'enumCompanyRankId', value: tier?.id }));
      dispatch(updateField({ field: 'enumCompanyRank', value: tier }));
    }
  };

  const handleSelectAccountManager = (value: string) => {
    const accountManager: any = accountManagers?.find((item: any) => item.name === value);

    if (value === null || accountManager) {
      const fieldsValue = {
        ...form.getFieldsValue(true),
        accountManagerId: accountManager?.id,
        accountManager: accountManager,
      };
      form.setFieldsValue(fieldsValue);

      dispatch(updateField({ field: 'accountManagerId', value: accountManager?.id }));
      dispatch(updateField({ field: 'accountManager', value: accountManager }));
    }
  };

  const handleFieldChange = (changedValues: any) => {
    Object.entries(changedValues).forEach(([key, value]) => {
      dispatch(updateField({ field: key, value }));
    });
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().includes(inputValue.toLowerCase());
  };

  useEffect(() => {
    form.setFieldsValue(company);
  }, [company]);

  return (
    <Form
      form={form}
      name='companyForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      layout='vertical'
      className='company-form'
    >
      <Row gutter={16}>
        {/* Persoana Juridica */}
        <Col xs={24} sm={12} md={8}>
          <Form.Item label='Tip' name='type' rules={[{ required: true, message: 'Introdu tipul!' }]}>
            <AutoComplete filterOption={handleFilter}>
              {['Persoana Fizica', 'Persoana Juridica'].map((typeItem, index) => (
                <Option key={index} value={typeItem}>
                  {typeItem}
                </Option>
              ))}
            </AutoComplete>
          </Form.Item>
        </Col>
        {company?.type === 'Persoana Juridica' && (
          <>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='CUI' name='cui' rules={[{ required: true, message: 'Introdu CUI!' }]}>
                <Input onChange={(e) => handleCUIChange(e.target.value)} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label='Numar inregistrare'
                name='registrationNumber'
                rules={[{ required: true, message: 'Introdu numar inregistrare!' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='IBAN' name='bankAccount'>
                <Input onChange={(e) => handleBankAccountChange(e.target.value)} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Banca' name='bank'>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Adresa' name='address' rules={[{ required: true, message: 'Introdu adresa!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Rank' name={['enumCompanyRank', 'name']}>
                <AutoComplete onSelect={handleSelectTier} filterOption={handleFilter}>
                  {tiers?.map((typeItem: { name: string }, index: any) => (
                    <Option key={index} value={typeItem.name}>
                      {typeItem.name}
                    </Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label='Manager de cont'
                name={['accountManager', 'name']}
                rules={[{ required: true, message: 'Introdu managerul de cont!' }]}
              >
                <AutoComplete onSelect={handleSelectAccountManager} filterOption={handleFilter}>
                  {accountManagers?.map((item, index) => (
                    <Option key={index} value={item?.name}>
                      {item?.name}
                    </Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Grup' name={['companyGroup', 'name']}>
                <AutoComplete onSelect={handleSelectGroupName} filterOption={handleFilter}>
                  {companyGroups?.map((typeItem: { name: string }, index: any) => (
                    <Option key={index} value={typeItem.name}>
                      {typeItem.name}
                    </Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Telefon' name='phone' rules={[{ required: true, message: 'Introdu telefon!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Email' name='email' rules={[{ required: true, message: 'Introdu email!' }]}>
                <Input />
              </Form.Item>
            </Col>
          </>
        )}

        {/* Persoana Fizica */}
        {company?.type === 'Persoana Fizica' && (
          <>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='CNP' name='cnp' rules={[{ required: true, message: 'Introdu CNP!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Adresa' name='address' rules={[{ required: true, message: 'Introdu adresa!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='IBAN' name='bankAccount'>
                <Input onChange={(e) => handleBankAccountChange(e.target.value)} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Banca' name='bank'>
                <Input />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label='Manager de cont'
                name={['accountManager', 'name']}
                rules={[{ required: true, message: 'Introdu managerul de cont!' }]}
              >
                <AutoComplete onSelect={handleSelectAccountManager} filterOption={handleFilter}>
                  {accountManagers?.map((item, index) => (
                    <Option key={index} value={item?.name}>
                      {item?.name}
                    </Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Telefon' name='phone' rules={[{ required: true, message: 'Introdu telefon!' }]}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Email' name='email' rules={[{ required: true, message: 'Introdu email!' }]}>
                <Input />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

export default ClientDetailsTab;
